const ENDPOINT_BASE = 'https://navigator-analytics.tweakwise.com/api';
const SESSION_COOKIE = 'tw_sid';
const PROFILE_COOKIE = 'tw_pid';
const REQUEST_ID_COOKIE = 'tw_req_id';
const SESSION_TTL_MINUTES = 30;
const PROFILE_TTL_MINUTES = 60 * 24 * 365 * 2;
const REQUEST_ID_TTL_MINUTES = 1;
// Re-emit SessionStart when the last emit is older than this so long-idle visitors whose tw_sid cookie kept getting refreshed do not become "orphan" events on Tweakwise's server (they expire the session after ~30 minutes of no traffic).
const SESSION_REFRESH_MINUTES = 20;
const LAST_SESSION_START_STORAGE_KEY = 'tw_session_start_at';

export class TweakwiseEvents {

    private static instanceKey: string | null = null;
    // In-memory fallback for cookie-blocked browsers and bfcache restores.
    private static cachedSessionKey: string | null = null;
    private static cachedProfileKey: string | null = null;
    // Last sessionKey we already emitted SessionStart for; drives whether bfcache restore needs a fresh SessionStart.
    private static emittedSessionKey: string | null = null;
    private static bfcacheHookInstalled = false;

    public static bootstrap(instanceKey: string | null | undefined): void {
        if (!instanceKey || instanceKey === '~') {
            // Clear stale key when navigating to an unconfigured page.
            this.instanceKey = null;
            return;
        }
        this.instanceKey = instanceKey;
        // Cookie absent => fresh session or 30-min expired; re-emit sessionstart. Also re-emit when the last SessionStart is stale even though the cookie kept getting refreshed, so subsequent events attribute to a live session on Tweakwise's side.
        const hadSession = this.getCookie(SESSION_COOKIE) !== null;
        const sessionKey = this.getOrCreateSessionKey();
        const profileKey = this.getOrCreateProfileKey();
        if (!hadSession || this.isLastSessionStartStale()) {
            this.cachedSessionKey = sessionKey;
            this.emittedSessionKey = sessionKey;
            this.markSessionStartEmitted();
            this.send('sessionstart', {
                SessionKey: sessionKey,
                ProfileKey: profileKey,
                Source: 'web',
            });
        } else {
            this.emittedSessionKey = sessionKey;
        }
        this.installBfcacheHook();
    }

    public static search(query: string): void {
        if (!this.instanceKey || !query) {
            return;
        }
        this.send('search', {
            SessionKey: this.getOrCreateSessionKey(),
            ProfileKey: this.getOrCreateProfileKey(),
            SearchTerm: query,
        });
    }

    public static pageImpression(requestId: string | null): void {
        if (!this.instanceKey) {
            return;
        }
        const sessionKey = this.getOrCreateSessionKey();
        // Our search proxy does not expose Tweakwise's twn-request-id response header; fall back to the sessionKey so the event still fires.
        this.send('pageimpression', {
            SessionKey: sessionKey,
            RequestId: requestId ?? sessionKey,
        });
    }

    public static itemClick(requestId: string | null, itemId: string | number): void {
        if (!this.instanceKey || !itemId) {
            return;
        }
        const sessionKey = this.getOrCreateSessionKey();
        this.send('itemclick', {
            SessionKey: sessionKey,
            ProfileKey: this.getOrCreateProfileKey(),
            RequestId: requestId ?? sessionKey,
            ItemId: String(itemId),
        });
    }

    // Stash the search request-id in a short-lived cookie so the immediately following detail request can be forwarded to api as X-Tweakwise-Request-Id and fire a server-side ItemClick attributed to the search.
    public static rememberSearchRequestId(requestId: string | null): void {
        if (!requestId) {
            return;
        }
        this.setCookie(REQUEST_ID_COOKIE, requestId, REQUEST_ID_TTL_MINUTES);
    }

    // bfcache-restored page with expired cookie needs a fresh SessionStart.
    private static installBfcacheHook(): void {
        if (this.bfcacheHookInstalled || typeof window === 'undefined') {
            return;
        }
        this.bfcacheHookInstalled = true;
        window.addEventListener('pageshow', (event: PageTransitionEvent) => {
            if (!event.persisted || !this.instanceKey) {
                return;
            }
            // Emit when the effective key changed since last SessionStart, or when the last emit is stale even though the key stayed the same.
            const sessionKey = this.getOrCreateSessionKey();
            if (sessionKey === this.emittedSessionKey && !this.isLastSessionStartStale()) {
                return;
            }
            this.emittedSessionKey = sessionKey;
            this.markSessionStartEmitted();
            this.send('sessionstart', {
                SessionKey: sessionKey,
                ProfileKey: this.getOrCreateProfileKey(),
                Source: 'web',
            });
        });
    }

    private static isLastSessionStartStale(): boolean {
        try {
            const raw = window.localStorage.getItem(LAST_SESSION_START_STORAGE_KEY);
            if (raw === null) {
                return true;
            }
            const at = Number.parseInt(raw, 10);
            if (!Number.isFinite(at)) {
                return true;
            }
            return Date.now() - at > SESSION_REFRESH_MINUTES * 60 * 1000;
        } catch {
            // localStorage blocked (private tab, cookie-block, etc.); treat as stale so a fresh SessionStart always fires.
            return true;
        }
    }

    private static markSessionStartEmitted(): void {
        try {
            window.localStorage.setItem(LAST_SESSION_START_STORAGE_KEY, String(Date.now()));
        } catch {
            // localStorage blocked; the bootstrap check will treat the next visit as stale, which is safe.
        }
    }

    private static send(event: string, payload: Record<string, unknown>): void {
        if (!this.instanceKey) {
            return;
        }
        const url = `${ENDPOINT_BASE}/${event}?instanceKey=${encodeURIComponent(this.instanceKey)}`;
        try {
            const body = JSON.stringify(payload);
            // text/plain is CORS-safelisted so sendBeacon skips preflight; Tweakwise parses the JSON body regardless of Content-Type.
            if (typeof navigator.sendBeacon === 'function') {
                const blob = new Blob([body], { type: 'text/plain' });
                if (navigator.sendBeacon(url, blob)) {
                    return;
                }
            }
            fetch(url, {
                method: 'POST',
                body,
                headers: { 'Content-Type': 'text/plain' },
                keepalive: true,
                mode: 'no-cors',
            }).catch(() => { /* fire-and-forget */ });
        } catch {
            /* telemetry never breaks UX */
        }
    }

    private static getOrCreateSessionKey(): string {
        let key = this.getCookie(SESSION_COOKIE);
        if (!key) {
            key = this.cachedSessionKey ?? this.uuid();
        }
        this.cachedSessionKey = key;
        this.setCookie(SESSION_COOKIE, key, SESSION_TTL_MINUTES);
        return key;
    }

    private static getOrCreateProfileKey(): string {
        let key = this.getCookie(PROFILE_COOKIE);
        if (!key) {
            key = this.cachedProfileKey ?? this.uuid();
        }
        this.cachedProfileKey = key;
        this.setCookie(PROFILE_COOKIE, key, PROFILE_TTL_MINUTES);
        return key;
    }

    private static getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    }

    private static setCookie(name: string, value: string, ttlMinutes: number): void {
        const expires = new Date(Date.now() + ttlMinutes * 60 * 1000).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
    }

    private static uuid(): string {
        const c = (window as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

}
